import type { CartagePayAccountState } from "@/schemas/cartagePayAccountState.schema"
import { shouldReadRfis } from "./walletAccountState.utils"

const walletOrgKey = (orgId: string) => `wallet:${orgId}:isWalletOrg`
const openWalletKey = (orgId: string) => `wallet:${orgId}:isWalletOpen`

const isMarked = (key: string) => {
    try {
        return localStorage.getItem(key) === "1"
    } catch {
        return false
    }
}

const setMark = ({ key, shouldMark }: { key: string; shouldMark: boolean }) => {
    try {
        if (shouldMark) {
            localStorage.setItem(key, "1")
        } else {
            localStorage.removeItem(key)
        }
    } catch {
        // localStorage can throw in a locked-down browser; nothing loads early.
    }
}

export function isMarkedWalletOrg(orgId: string): boolean {
    if (!orgId) return false
    return isMarked(walletOrgKey(orgId))
}

export function isMarkedOpenWallet(orgId: string): boolean {
    if (!orgId) return false
    return isMarked(openWalletKey(orgId))
}

export function setWalletOrgMark({
    orgId,
    isWalletOrg,
    isWalletOpen,
}: {
    orgId: string
    isWalletOrg: boolean
    isWalletOpen: boolean
}) {
    if (!orgId) return
    setMark({ key: walletOrgKey(orgId), shouldMark: isWalletOrg })
    setMark({
        key: openWalletKey(orgId),
        shouldMark: isWalletOrg && isWalletOpen,
    })
}

interface OnboardingSnapshot {
    access: string
    state: {
        account_state: CartagePayAccountState
        wallet_provisioned: boolean // boolean-naming-exempt: external contract — cartage-pay onboarding state field
    } | null
}

export const OPEN_WALLET_SNAPSHOT: OnboardingSnapshot = {
    access: "enabled",
    state: {
        account_state: "active",
        wallet_provisioned: true, // boolean-naming-exempt: external contract — cartage-pay onboarding state field
    },
}

export function walletWarmPlan({ access, state }: OnboardingSnapshot) {
    if (access !== "enabled" || state === null) {
        return {
            shouldReadRfis: false,
            shouldReadBalances: false,
            shouldReadHome: false,
        }
    }
    const isActive = state.account_state === "active"
    const isPaused = state.account_state === "paused"
    return {
        shouldReadRfis: shouldReadRfis(state.account_state),
        shouldReadBalances: state.wallet_provisioned && (isActive || isPaused),
        shouldReadHome: state.wallet_provisioned && isActive,
    }
}
